import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { collection, getDocs, doc, setDoc, updateDoc, deleteDoc, serverTimestamp, query, where } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { IoSearchOutline, IoCloseCircle, IoCheckmarkCircle } from 'react-icons/io5';
import { HiOutlinePencilSquare, HiOutlineTrash, HiPlus } from 'react-icons/hi2';
import useAdminAuth from '../../../hooks/useAdminAuth';

const ROLE_LABELS = { teacher: '강사', student: '학생', assistant: '조교', parent: '학부모' };
const ROLE_COLORS = { teacher: '#4A7CFE', student: '#22C55E', assistant: '#F59E0B', parent: '#A855F7' };
const ADMIN_ROLE_LABELS = { superadmin: '슈퍼관리자', teacher_all: '강사 (모든 클래스)', teacher_own: '강사 (자기 클래스)', assistant: '조교 (읽기전용)' };

const AdminUsersPage = () => {
  const { canManageUsers, canDelete, isReadOnly, isSuperAdmin } = useAdminAuth();
  const [users, setUsers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', school: '', role: 'student', grade: '', adminRole: '', loginId: '', password: '' });

  // 아이디 중복 체크
  const [idChecked, setIdChecked] = useState(false);
  const [idChecking, setIdChecking] = useState(false);
  const [idAvailable, setIdAvailable] = useState(false);

  // 학부모 - 학생 검색
  const [studentSearch, setStudentSearch] = useState('');
  const [studentResults, setStudentResults] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentSearching, setStudentSearching] = useState(false);
  const [showStudentResults, setShowStudentResults] = useState(false);

  const handleStudentSearch = async () => {
    if (!studentSearch.trim()) return;
    setStudentSearching(true);
    setShowStudentResults(true);
    try {
      const q = query(collection(db, 'users'), where('role', '==', 'student'));
      const snap = await getDocs(q);
      const keyword = studentSearch.trim().toLowerCase();
      const students = snap.docs
        .map((d) => ({ uid: d.id, ...d.data() }))
        .filter((s) =>
          (s.name || '').toLowerCase().includes(keyword) ||
          (s.loginId || '').toLowerCase().includes(keyword)
        );
      setStudentResults(students);
    } catch (err) {
      console.error('학생 검색 실패:', err);
      setStudentResults([]);
    } finally {
      setStudentSearching(false);
    }
  };

  // 학생 - 클래스 선택
  const [classList, setClassList] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);

  const loadClasses = async () => {
    const snap = await getDocs(collection(db, 'classes'));
    setClassList(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  const toggleClass = (cls) => {
    setSelectedClasses((prev) =>
      prev.find((c) => c.id === cls.id)
        ? prev.filter((c) => c.id !== cls.id)
        : [...prev, cls]
    );
  };

  // 비밀번호 변경
  const [pwModal, setPwModal] = useState(null); // user uid
  const [newPw, setNewPw] = useState('');

  const handleChangePw = async () => {
    if (!newPw || newPw.length < 6) return;
    await updateDoc(doc(db, 'users', pwModal), { password: newPw });
    setPwModal(null);
    setNewPw('');
    load();
  };

  const load = async () => {
    const snap = await getDocs(collection(db, 'users'));
    const all = snap.docs
      .map((d) => ({ uid: d.id, ...d.data() }))
      .filter((u) => u.role && u.role !== 'admin');
    setUsers(all);
    setLoaded(true);
  };

  useEffect(() => { load(); }, []);

  const filtered = users
    .filter((u) => filter === 'all' || u.role === filter)
    .filter((u) => {
      if (!search) return true;
      const s = search.toLowerCase();
      return (u.name || '').toLowerCase().includes(s) || (u.loginId || '').toLowerCase().includes(s);
    });

  const openCreate = () => {
    setEditingUser(null);
    setForm({ name: '', phone: '', school: '', role: 'student', grade: '', adminRole: '', loginId: '', password: '' });
    setIdChecked(false);
    setIdAvailable(false);
    setSelectedStudent(null);
    setStudentSearch('');
    setStudentResults([]);
    setShowStudentResults(false);
    setSelectedClasses([]);
    loadClasses();
    setShowModal(true);
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setForm({
      name: user.name || '',
      phone: user.phone || '',
      school: user.school || '',
      role: user.role || 'student',
      grade: user.grade || '',
      adminRole: user.adminRole || '',
      loginId: user.loginId || '',
      password: '',
    });
    setIdChecked(true);
    setIdAvailable(true);
    // 학부모인 경우 기존 연결 학생 복원
    if (user.role === 'parent' && user.children && user.children.length > 0) {
      setSelectedStudent({ uid: user.children[0], name: user.childrenNames?.[0] || '' });
    } else {
      setSelectedStudent(null);
    }
    setStudentSearch('');
    setStudentResults([]);
    setShowStudentResults(false);
    // 학생인 경우 기존 클래스 복원
    if (user.role === 'student' && user.classes && user.classes.length > 0) {
      setSelectedClasses(user.classes.map((id, i) => ({ id, name: user.classNames?.[i] || id })));
    } else {
      setSelectedClasses([]);
    }
    loadClasses();
    setShowModal(true);
  };

  const handleCheckDuplicate = async () => {
    if (!form.loginId.trim()) return;
    setIdChecking(true);
    try {
      const q = query(collection(db, 'users'), where('loginId', '==', form.loginId.trim()));
      const snap = await getDocs(q);
      const isSelf = editingUser && snap.docs.length === 1 && snap.docs[0].id === editingUser.uid;
      if (snap.empty || isSelf) {
        setIdChecked(true);
        setIdAvailable(true);
      } else {
        setIdChecked(true);
        setIdAvailable(false);
      }
    } catch (err) {
      console.error('중복 체크 실패:', err);
    } finally {
      setIdChecking(false);
    }
  };

  const handleSave = async () => {
    if (!form.name || !form.role) return;
    if (form.loginId && (!idChecked || !idAvailable)) return;

    const data = {
      name: form.name,
      phone: form.phone,
      role: form.role,
    };

    if (form.loginId) data.loginId = form.loginId.trim();
    if (form.password) data.password = form.password;

    if (form.role === 'student') {
      data.school = form.school;
      data.grade = form.grade;
      data.classes = selectedClasses.map((c) => c.id);
      data.classNames = selectedClasses.map((c) => c.name);
    } else if (form.role === 'teacher') {
      data.adminRole = form.adminRole || 'teacher_own';
    } else if (form.role === 'assistant') {
      data.school = form.school;
      if (form.adminRole) {
        data.adminRole = form.adminRole;
      }
    } else if (form.role === 'parent') {
      if (selectedStudent) {
        data.children = [selectedStudent.uid];
        data.childrenNames = [selectedStudent.name];
      }
    }

    if (editingUser) {
      await updateDoc(doc(db, 'users', editingUser.uid), data);
    } else {
      const id = `user-${Date.now()}`;
      data.createdAt = serverTimestamp();
      data.profileImage = '';
      await setDoc(doc(db, 'users', id), data);
    }

    setShowModal(false);
    load();
  };

  const handleDelete = async (user) => {
    const isTeacher = user.role === 'teacher';
    const msg = isTeacher
      ? `'${user.name}' 강사를 삭제하시겠습니까?\n\n⚠️ 해당 강사의 클래스 및 관리 정보가 모두 삭제됩니다.`
      : `'${user.name}' 사용자를 삭제하시겠습니까?`;

    if (!window.confirm(msg)) return;
    if (isTeacher && !window.confirm('정말 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) return;

    // 강사인 경우 담당 클래스도 삭제
    if (isTeacher) {
      const classSnap = await getDocs(query(collection(db, 'classes'), where('teacherId', '==', user.uid)));
      for (const cls of classSnap.docs) {
        await deleteDoc(doc(db, 'classes', cls.id));
      }
    }

    await deleteDoc(doc(db, 'users', user.uid));
    load();
  };

  const handleToggleStatus = async (user) => {
    const newStatus = user.status === 'approved' ? 'pending' : 'approved';
    await updateDoc(doc(db, 'users', user.uid), { status: newStatus });
    load();
  };

  const formatDate = (val) => {
    if (!val) return '-';
    if (typeof val === 'string') return val.split('T')[0];
    if (val.seconds) return new Date(val.seconds * 1000).toLocaleDateString('ko-KR');
    return '-';
  };

  return (
    <Container>
      <PageHeader>
        <div>
          <PageTitle>사용자 관리</PageTitle>
          <PageDesc>전체 {users.length}명의 사용자</PageDesc>
        </div>
        <HeaderRight>
          {canManageUsers && (
            <AddButton onClick={openCreate}>
              <HiPlus size={16} /> 사용자 추가
            </AddButton>
          )}
        </HeaderRight>
      </PageHeader>

      <ToolBar>
        <SearchBox>
          <IoSearchOutline size={16} color="#9CA3AF" />
          <SearchInput
            placeholder="이름, 아이디로 검색..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoComplete="off"
          />
        </SearchBox>
        <FilterGroup>
          {['all', 'teacher', 'student', 'assistant', 'parent'].map((f) => (
            <FilterBtn key={f} $active={filter === f} onClick={() => setFilter(f)}>
              {f === 'all' ? '전체' : ROLE_LABELS[f]}
              <FilterCount>{f === 'all' ? users.length : users.filter((u) => u.role === f).length}</FilterCount>
            </FilterBtn>
          ))}
        </FilterGroup>
      </ToolBar>

      <TableCard>
        <Table>
          <thead>
            <tr>
              <Th>이름</Th>
              <Th>아이디</Th>
              <Th>로그인 수단</Th>
              <Th>역할</Th>
              <Th>권한</Th>
              <Th>전화번호</Th>
              <Th>소속</Th>
              <Th>승인</Th>
              <Th>가입일</Th>
              {canManageUsers && <Th></Th>}
            </tr>
          </thead>
          <tbody>
            {!loaded ? null : filtered.length === 0 ? (
              <tr><Td colSpan={canManageUsers ? 10 : 9} style={{ textAlign: 'center', color: '#9CA3AF', padding: '32px' }}>사용자가 없습니다</Td></tr>
            ) : (
              filtered.map((user) => (
                <tr key={user.uid}>
                  <Td><UserName>{user.name}</UserName></Td>
                  <Td><IdText>{user.loginId || '-'}</IdText></Td>
                  <Td>
                    <LoginMethodCell>
                      {user.loginId && <MethodBadge $type="id">아이디</MethodBadge>}
                      {(user.provider === 'google' || user.linkedSocialUid?.startsWith('google') || user.linkedProviders?.includes('google')) && <MethodBadge $type="google">구글</MethodBadge>}
                      {(user.provider === 'kakao' || user.linkedSocialUid?.startsWith('kakao') || user.linkedProviders?.includes('kakao')) && <MethodBadge $type="kakao">카카오</MethodBadge>}
                      {!user.loginId && !user.provider && !user.linkedSocialUid && '-'}
                      {isSuperAdmin && user.loginId && (
                        <PwChangeBtn onClick={() => { setPwModal(user.uid); setNewPw(''); }}>PW변경</PwChangeBtn>
                      )}
                    </LoginMethodCell>
                  </Td>
                  <Td><RoleBadge $color={ROLE_COLORS[user.role]}>{ROLE_LABELS[user.role] || user.role}</RoleBadge></Td>
                  <Td>{user.adminRole ? <AdminBadge>{ADMIN_ROLE_LABELS[user.adminRole] || '-'}</AdminBadge> : (ROLE_LABELS[user.role] || '-')}</Td>
                  <Td>{user.phone || '-'}</Td>
                  <Td>{(user.role === 'teacher' || user.role === 'assistant') ? '1등급' : (user.school || '-')}{user.grade ? ` (${user.grade})` : ''}</Td>
                  <Td>
                    <ToggleWrap onClick={() => canManageUsers && handleToggleStatus(user)} $clickable={canManageUsers}>
                      <ToggleTrack $on={user.status === 'approved'}>
                        <ToggleThumb $on={user.status === 'approved'} />
                      </ToggleTrack>
                      <ToggleLabel $on={user.status === 'approved'}>
                        {user.status === 'approved' ? '승인' : '대기'}
                      </ToggleLabel>
                    </ToggleWrap>
                  </Td>
                  <Td>{formatDate(user.createdAt)}</Td>
                  {canManageUsers && (
                    <Td>
                      <ActionGroup>
                        <ActionBtn title="수정" onClick={() => openEdit(user)}><HiOutlinePencilSquare size={16} /></ActionBtn>
                        {canDelete && (
                          <ActionBtn title="삭제" $danger onClick={() => handleDelete(user)}><HiOutlineTrash size={16} /></ActionBtn>
                        )}
                      </ActionGroup>
                    </Td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </Table>
        <TableFooter>
          <FooterText>총 {filtered.length}명</FooterText>
        </TableFooter>
      </TableCard>

      {showModal && (
        <Overlay onClick={() => setShowModal(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalTitle>{editingUser ? '사용자 수정' : '사용자 추가'}</ModalTitle>

            <ModalLabel>역할 *</ModalLabel>
            <RoleGrid>
              {Object.entries(ROLE_LABELS).map(([key, label]) => (
                <RoleBtn
                  key={key}
                  type="button"
                  $active={form.role === key}
                  $color={ROLE_COLORS[key]}
                  onClick={() => setForm({ ...form, role: key, adminRole: key === 'teacher' ? 'teacher_own' : '', loginId: form.loginId, password: '' })}
                >
                  {label}
                </RoleBtn>
              ))}
            </RoleGrid>

            <ModalLabel>이름 *</ModalLabel>
            <ModalInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="" />

            <ModalLabel>아이디</ModalLabel>
            <IdRow>
              <ModalInput
                value={form.loginId}
                onChange={(e) => { setForm({ ...form, loginId: e.target.value }); setIdChecked(false); setIdAvailable(false); }}
                placeholder=""
                style={{ flex: 1 }}
              />
              <DupBtn type="button" onClick={handleCheckDuplicate} disabled={idChecking || !form.loginId.trim()}>
                {idChecking ? '확인중' : '중복체크'}
              </DupBtn>
            </IdRow>
            {idChecked && (
              <DupResult $ok={idAvailable}>
                {idAvailable ? '사용 가능한 아이디입니다.' : '이미 사용 중인 아이디입니다.'}
              </DupResult>
            )}

            <ModalLabel>비밀번호 {editingUser ? '(변경 시에만 입력)' : ''}</ModalLabel>
            <ModalInput type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="" />

            <ModalLabel>전화번호</ModalLabel>
            <ModalInput value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="" />

            {(form.role === 'student' || form.role === 'assistant') && (
              <>
                <ModalLabel>소속 학교/학원</ModalLabel>
                <ModalInput value={form.school} onChange={(e) => setForm({ ...form, school: e.target.value })} placeholder="" />
              </>
            )}

            {form.role === 'student' && (
              <>
                <ModalLabel>학년</ModalLabel>
                <ModalInput value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} placeholder="" />

                <ModalLabel>클래스 선택</ModalLabel>
                {classList.length === 0 ? (
                  <StudentResultEmpty>등록된 클래스가 없습니다</StudentResultEmpty>
                ) : (
                  <ClassSelectGrid>
                    {classList.map((cls) => {
                      const isSelected = selectedClasses.find((c) => c.id === cls.id);
                      return (
                        <ClassSelectItem
                          key={cls.id}
                          type="button"
                          $selected={!!isSelected}
                          $color={cls.color || '#4A7CFE'}
                          onClick={() => toggleClass(cls)}
                        >
                          <ClassSelectInfo>
                            <ClassSelectName>{cls.name}</ClassSelectName>
                            <ClassSelectMeta>{cls.teacherName || '-'}{cls.room ? ` · ${cls.room}` : ''}</ClassSelectMeta>
                          </ClassSelectInfo>
                          {isSelected && <IoCheckmarkCircle size={18} color={cls.color || '#4A7CFE'} />}
                        </ClassSelectItem>
                      );
                    })}
                  </ClassSelectGrid>
                )}
                {selectedClasses.length > 0 && (
                  <ClassSelectCount>{selectedClasses.length}개 클래스 선택됨</ClassSelectCount>
                )}
              </>
            )}

            {form.role === 'parent' && (
              <>
                <ModalLabel>수강 학생 *</ModalLabel>
                {selectedStudent ? (
                  <SelectedChip>
                    <ChipInfo>
                      <ChipName>{selectedStudent.name}</ChipName>
                      {selectedStudent.loginId && <ChipId>@{selectedStudent.loginId}</ChipId>}
                    </ChipInfo>
                    <ChipRemove type="button" onClick={() => setSelectedStudent(null)}>
                      <IoCloseCircle size={18} />
                    </ChipRemove>
                  </SelectedChip>
                ) : (
                  <>
                    <StudentSearchRow>
                      <ModalInput
                        value={studentSearch}
                        onChange={(e) => setStudentSearch(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleStudentSearch(); } }}
                        placeholder="학생 이름 또는 아이디로 검색"
                        style={{ flex: 1 }}
                      />
                      <DupBtn type="button" onClick={handleStudentSearch} disabled={studentSearching || !studentSearch.trim()}>
                        {studentSearching ? '검색중' : '검색'}
                      </DupBtn>
                    </StudentSearchRow>
                    {showStudentResults && (
                      <StudentResultList>
                        {studentSearching ? (
                          <StudentResultEmpty>검색 중...</StudentResultEmpty>
                        ) : studentResults.length === 0 ? (
                          <StudentResultEmpty>검색 결과가 없습니다</StudentResultEmpty>
                        ) : (
                          studentResults.map((s) => {
                            const hasDup = studentResults.filter((r) => r.name === s.name).length > 1;
                            return (
                              <StudentResultItem key={s.uid} onClick={() => {
                                setSelectedStudent(s);
                                setShowStudentResults(false);
                                setStudentSearch('');
                              }}>
                                <span style={{ fontWeight: 600 }}>{s.name}</span>
                                {s.loginId && <span style={{ color: '#4A7CFE', fontFamily: 'monospace', fontSize: 12 }}>@{s.loginId}</span>}
                                {hasDup && s.school && <span style={{ color: '#9CA3AF', fontSize: 12 }}>{s.school}</span>}
                              </StudentResultItem>
                            );
                          })
                        )}
                      </StudentResultList>
                    )}
                  </>
                )}
              </>
            )}

            {(form.role === 'teacher' || form.role === 'assistant') && (
              <>
                <Divider />
                <SectionLabel>관리자 권한 설정</SectionLabel>
                <ModalLabel>관리자 권한</ModalLabel>
                <Select value={form.adminRole} onChange={(e) => setForm({ ...form, adminRole: e.target.value })}>
                  {form.role === 'teacher' && (
                    <>
                      <option value="teacher_all">모든 클래스 관리</option>
                      <option value="teacher_own">자기 클래스만 관리</option>
                    </>
                  )}
                  {form.role === 'assistant' && (
                    <>
                      <option value="">없음 (일반 사용자)</option>
                      <option value="assistant">조교 (읽기 전용)</option>
                    </>
                  )}
                </Select>
              </>
            )}

            <ModalActions>
              <CancelBtn onClick={() => setShowModal(false)}>취소</CancelBtn>
              <SaveBtn onClick={handleSave} disabled={!form.name || !form.role || (form.loginId && !idChecked)}>
                {editingUser ? '수정' : '추가'}
              </SaveBtn>
            </ModalActions>
          </Modal>
        </Overlay>
      )}

      {pwModal && (
        <Overlay onClick={() => setPwModal(null)}>
          <PwModal onClick={(e) => e.stopPropagation()}>
            <ModalTitle>비밀번호 변경</ModalTitle>
            <ModalLabel>새 비밀번호 (6자 이상)</ModalLabel>
            <ModalInput
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              placeholder=""
              autoFocus
              autoComplete="new-password"
            />
            <ModalActions>
              <CancelBtn onClick={() => setPwModal(null)}>취소</CancelBtn>
              <SaveBtn onClick={handleChangePw} disabled={!newPw || newPw.length < 6}>변경</SaveBtn>
            </ModalActions>
          </PwModal>
        </Overlay>
      )}
    </Container>
  );
};

export default AdminUsersPage;

const Container = styled.div``;
const PageHeader = styled.div`display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:20px;`;
const PageTitle = styled.h1`font-size:22px;font-weight:700;color:#111827;`;
const PageDesc = styled.p`font-size:13px;color:#9CA3AF;margin-top:2px;`;
const HeaderRight = styled.div`display:flex;gap:8px;`;
const AddButton = styled.button`display:flex;align-items:center;gap:6px;padding:8px 16px;border-radius:8px;background:#4A7CFE;color:white;font-size:13px;font-weight:600;&:hover{background:#3B6DE8;}`;

const ToolBar = styled.div`display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;gap:16px;`;
const SearchBox = styled.div`display:flex;align-items:center;gap:8px;padding:8px 12px;background:white;border:1px solid #E5E7EB;border-radius:8px;width:280px;`;
const SearchInput = styled.input`border:none;background:none;font-size:13px;width:100%;&::placeholder{color:#9CA3AF;}&:focus{outline:none;}`;
const FilterGroup = styled.div`display:flex;gap:4px;`;
const FilterBtn = styled.button`
  display:flex;align-items:center;gap:6px;padding:7px 14px;border-radius:8px;font-size:13px;
  font-weight:${({ $active }) => $active ? 600 : 400};
  color:${({ $active }) => $active ? '#4A7CFE' : '#6B7280'};
  background:${({ $active }) => $active ? '#EEF2FF' : 'transparent'};
  border:1px solid ${({ $active }) => $active ? '#C7D2FE' : 'transparent'};
  &:hover{background:${({ $active }) => $active ? '#EEF2FF' : '#F9FAFB'};}
`;
const FilterCount = styled.span`font-size:11px;background:#F3F4F6;padding:1px 6px;border-radius:10px;color:#6B7280;`;

const TableCard = styled.div`background:white;border-radius:12px;border:1px solid #EAEDF3;overflow:hidden;`;
const Table = styled.table`width:100%;border-collapse:collapse;`;
const Th = styled.th`text-align:left;padding:12px 16px;font-size:12px;font-weight:600;color:#9CA3AF;background:#F9FAFB;border-bottom:1px solid #EAEDF3;white-space:nowrap;`;
const Td = styled.td`padding:12px 16px;font-size:13px;color:#374151;border-bottom:1px solid #F3F4F6;white-space:nowrap;`;
const UserName = styled.span`font-weight:600;color:#111827;`;
const AdminBadge = styled.span`display:inline-block;margin-left:6px;padding:1px 6px;border-radius:4px;font-size:10px;font-weight:600;background:#EEF2FF;color:#4A7CFE;vertical-align:middle;`;
const IdText = styled.span`color:#6B7280;font-family:monospace;font-size:12px;`;
const RoleBadge = styled.span`display:inline-block;padding:4px 10px;border-radius:4px;font-size:12px;font-weight:600;background:${({ $color }) => $color}15;color:${({ $color }) => $color};`;
const ActionGroup = styled.div`display:flex;gap:4px;`;
const ActionBtn = styled.button`padding:6px;border-radius:6px;color:${({ $danger }) => $danger ? '#EF4444' : '#9CA3AF'};&:hover{background:${({ $danger }) => $danger ? '#FEF2F2' : '#F3F4F6'};color:${({ $danger }) => $danger ? '#DC2626' : '#374151'};}`;
const TableFooter = styled.div`display:flex;justify-content:space-between;align-items:center;padding:12px 16px;border-top:1px solid #EAEDF3;`;
const FooterText = styled.span`font-size:13px;color:#9CA3AF;`;

// Modal
const Overlay = styled.div`position:fixed;inset:0;background:rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;z-index:50;`;
const Modal = styled.div`background:white;border-radius:16px;padding:24px;width:480px;max-width:90vw;max-height:90vh;overflow-y:auto;`;
const ModalTitle = styled.h2`font-size:18px;font-weight:700;color:#1E3A5F;margin-bottom:16px;`;
const ModalLabel = styled.label`display:block;font-size:13px;font-weight:600;color:#374151;margin-bottom:6px;margin-top:14px;`;
const ModalInput = styled.input`width:100%;padding:10px 12px;border:1px solid #E5E7EB;border-radius:8px;font-size:14px;color:#111827;&:focus{border-color:#4A7CFE;outline:none;}`;
const Select = styled.select`width:100%;padding:10px 12px;border:1px solid #E5E7EB;border-radius:8px;font-size:14px;color:#111827;`;

const IdRow = styled.div`display:flex;gap:8px;`;
const DupBtn = styled.button`padding:10px 14px;border-radius:8px;background:#4A7CFE;color:white;font-size:13px;font-weight:600;white-space:nowrap;&:disabled{opacity:0.5;}&:hover:not(:disabled){background:#3B6DE8;}`;
const DupResult = styled.p`font-size:12px;margin-top:4px;font-weight:500;color:${({ $ok }) => $ok ? '#22C55E' : '#EF4444'};`;

const StudentSearchRow = styled.div`display:flex;gap:8px;`;
const StudentResultList = styled.div`margin-top:8px;border:1px solid #E5E7EB;border-radius:8px;max-height:160px;overflow-y:auto;`;
const StudentResultItem = styled.button`width:100%;display:flex;align-items:center;gap:8px;padding:10px 12px;font-size:13px;color:#374151;text-align:left;border-bottom:1px solid #F3F4F6;&:last-child{border:none;}&:hover{background:#EEF2FF;}`;
const StudentResultEmpty = styled.p`padding:12px;text-align:center;font-size:13px;color:#9CA3AF;`;
const SelectedChip = styled.div`display:flex;align-items:center;justify-content:space-between;padding:10px 12px;border:2px solid #4A7CFE;border-radius:8px;background:#EEF2FF;`;
const ChipInfo = styled.div`display:flex;align-items:center;gap:8px;`;
const ChipName = styled.span`font-size:14px;font-weight:600;color:#111827;`;
const ChipId = styled.span`font-size:12px;color:#4A7CFE;font-family:monospace;`;
const ChipRemove = styled.button`color:#9CA3AF;display:flex;&:hover{color:#EF4444;}`;

const ClassSelectGrid = styled.div`display:flex;flex-direction:column;gap:6px;max-height:200px;overflow-y:auto;`;
const ClassSelectItem = styled.button`
  width:100%;display:flex;align-items:center;justify-content:space-between;padding:10px 12px;border-radius:8px;text-align:left;
  border:2px solid ${({ $selected, $color }) => $selected ? $color : '#E5E7EB'};
  background:${({ $selected, $color }) => $selected ? `${$color}10` : 'white'};
  transition:all 0.15s;
`;
const ClassSelectInfo = styled.div``;
const ClassSelectName = styled.span`display:block;font-size:13px;font-weight:600;color:#111827;`;
const ClassSelectMeta = styled.span`display:block;font-size:11px;color:#9CA3AF;margin-top:2px;`;
const ClassSelectCount = styled.p`font-size:12px;font-weight:600;color:#4A7CFE;margin-top:6px;`;

const LoginMethodCell = styled.div`display:flex;align-items:center;gap:4px;flex-wrap:wrap;`;
const METHOD_COLORS = { id: { bg: '#EEF2FF', color: '#4A7CFE' }, google: { bg: '#FEF2F2', color: '#EA4335' }, kakao: { bg: '#FEF9C3', color: '#8B6914' } };
const MethodBadge = styled.span`display:inline-block;padding:2px 8px;border-radius:4px;font-size:11px;font-weight:600;background:${({ $type }) => METHOD_COLORS[$type]?.bg || '#F3F4F6'};color:${({ $type }) => METHOD_COLORS[$type]?.color || '#6B7280'};`;
const PwChangeBtn = styled.button`padding:3px 8px;border-radius:4px;font-size:11px;font-weight:600;color:#4A7CFE;background:#EEF2FF;border:1px solid #C7D2FE;&:hover{background:#DDE5FF;}`;
const PwModal = styled.div`background:white;border-radius:16px;padding:24px;width:360px;max-width:90vw;`;

const RoleGrid = styled.div`display:grid;grid-template-columns:repeat(4,1fr);gap:8px;`;
const RoleBtn = styled.button`
  padding:10px;border-radius:8px;font-size:13px;font-weight:600;text-align:center;
  border:2px solid ${({ $active, $color }) => $active ? $color : '#E5E7EB'};
  background:${({ $active, $color }) => $active ? `${$color}10` : 'white'};
  color:${({ $active, $color }) => $active ? $color : '#6B7280'};
`;

const ToggleWrap = styled.div`display:flex;align-items:center;gap:8px;cursor:${({ $clickable }) => $clickable ? 'pointer' : 'default'};`;
const ToggleTrack = styled.div`width:36px;height:20px;border-radius:10px;background:${({ $on }) => $on ? '#22C55E' : '#D1D5DB'};position:relative;transition:background 0.2s;`;
const ToggleThumb = styled.div`width:16px;height:16px;border-radius:50%;background:white;position:absolute;top:2px;left:${({ $on }) => $on ? '18px' : '2px'};transition:left 0.2s;box-shadow:0 1px 3px rgba(0,0,0,0.2);`;
const ToggleLabel = styled.span`font-size:12px;font-weight:600;color:${({ $on }) => $on ? '#22C55E' : '#F59E0B'};`;

const Divider = styled.div`height:1px;background:#E5E7EB;margin:18px 0 4px;`;
const SectionLabel = styled.p`font-size:14px;font-weight:700;color:#111827;margin-bottom:4px;`;

const ModalActions = styled.div`display:flex;gap:8px;justify-content:flex-end;margin-top:20px;`;
const CancelBtn = styled.button`padding:10px 20px;border-radius:8px;border:1px solid #E5E7EB;font-size:14px;color:#374151;&:hover{background:#F9FAFB;}`;
const SaveBtn = styled.button`padding:10px 20px;border-radius:8px;background:#4A7CFE;color:white;font-size:14px;font-weight:600;&:hover{background:#3B6DE8;}&:disabled{opacity:0.5;}`;
